import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Landmark, TrendingUp, Building2, Briefcase,
  Sparkles, Brain, FileSignature, Users, Search,
  Activity, BookOpen, ListTodo, BarChart3, MessageSquare, Calculator, Zap, Globe,
  ArrowUpRight,
} from "lucide-react";

const CAPABILITIES = [
  { num: "01", label: "Executive Consulting Operations", icon: Briefcase, desc: "Strategic workflows, advisory engagements, institutional coordination, board-level communications." },
  { num: "02", label: "AI Proposal & Document Automation", icon: FileSignature, desc: "Proposals, MOUs, grant applications, business plans, concept notes, partnership agreements." },
  { num: "03", label: "Institutional CRM", icon: Users, desc: "Clients, investors, government stakeholders, partners — pipelines, history, intelligence." },
  { num: "04", label: "Government & Public Sector Engagement", icon: Landmark, desc: "Ministries, PPP frameworks, diplomatic coordination, national project support." },
  { num: "05", label: "Investor Relations & Capital Raising", icon: TrendingUp, desc: "Fundraising coordination, pipeline, due diligence, investor reporting." },
  { num: "06", label: "Strategic Research & Intelligence", icon: Search, desc: "Market intelligence, competitor analysis, policy monitoring, AI-assisted recommendations." },
  { num: "07", label: "Knowledge & Institutional Memory", icon: BookOpen, desc: "Searchable archives, SOPs, templates, governance frameworks, policies." },
  { num: "08", label: "Operations & Workflow Management", icon: ListTodo, desc: "Tasks, approvals, KPIs, cross-functional execution, productivity." },
  { num: "09", label: "Reporting & Analytics", icon: BarChart3, desc: "Executive, board, investor reports; KPI dashboards; impact reporting." },
  { num: "10", label: "Business Development & Growth", icon: Activity, desc: "Lead pipelines, market expansion, strategic partnerships, positioning." },
  { num: "11", label: "AI Communication & Content", icon: MessageSquare, desc: "Executive messaging, speech drafts, announcements, stakeholder correspondence." },
  { num: "12", label: "Finance & Administrative Support", icon: Calculator, desc: "Budgets, forecasts, expense, vendor coordination, internal reporting." },
  { num: "13", label: "Project & Program Management", icon: Briefcase, desc: "Milestones, resources, deliverables, timelines, risk monitoring." },
  { num: "14", label: "AI Decision Support", icon: Brain, desc: "Predictive insights, scenario planning, operational forecasting." },
  { num: "15", label: "Enterprise Collaboration", icon: Users, desc: "Shared workspaces, internal + partner collaboration, executive coordination." },
  { num: "16", label: "Infrastructure & Integration", icon: Globe, desc: "CRM, workflow, AI, cloud, APIs, third-party institutional systems." },
  { num: "17", label: "Branding & Institutional Visibility", icon: Sparkles, desc: "Corporate visibility, strategic positioning, reputation management." },
  { num: "18", label: "Automation & Workflow Triggers", icon: Zap, desc: "Trigger-based automations, approvals, CRM-to-operations workflows." },
];

const SUITES = [
  {
    id: "government",
    name: "Pandora Government Suite",
    audience: "Ministries · Agencies · Public Institutions · National Strategic Projects",
    icon: Landmark,
    color: "#3B82F6",
    bullets: ["Ministry tracking & engagement", "PPP framework workflows", "Protocol-aware briefings", "Diplomatic coordination"],
  },
  {
    id: "investor",
    name: "Pandora Investor Suite",
    audience: "Investors · DFIs · Fundraising Teams · Institutional Capital",
    icon: TrendingUp,
    color: "#D4AF37",
    bullets: ["Investor dashboards", "Capital pipeline management", "Term-sheet positioning", "Shareholder reporting"],
  },
  {
    id: "enterprise",
    name: "Pandora Enterprise Suite",
    audience: "SMEs · Corporations · Executive Teams · Operations",
    icon: Building2,
    color: "#22C55E",
    bullets: ["Executive operations", "KPI dashboards", "Cross-functional workflows", "Department coordination"],
  },
  {
    id: "advisory",
    name: "Pandora Advisory Suite",
    audience: "Consulting Firms · Strategic Advisors · Professional Services",
    icon: Briefcase,
    color: "#A855F7",
    bullets: ["Engagement management", "Institutional proposals", "Knowledge & IP retention", "Advisor coordination"],
  },
];

export default function Platform() {
  return (
    <div className="p-8 max-w-7xl mx-auto space-y-12" data-testid="platform-page">
      {/* Hero */}
      <motion.section
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="relative overflow-hidden border border-white/10 bg-[#0A111F] p-12 nexus-topo"
      >
        <div className="label-eyebrow mb-3">// Platform Overview</div>
        <h1 className="font-display text-5xl lg:text-6xl text-white tracking-tight max-w-4xl leading-tight">
          Africa's AI-powered <span style={{ color: "var(--gold-500)" }}>institutional intelligence</span> & operational infrastructure ecosystem.
        </h1>
        <p className="text-slate-400 mt-6 max-w-2xl leading-relaxed">
          Pandora Nexus combines Artificial Intelligence, Executive Operations, Institutional Intelligence, Strategic Advisory,
          Workflow Automation, Government Engagement, Investor Relations, and Enterprise Management into one integrated
          operational platform — developed by{" "}
          <a href="https://www.pandoramanagementconsult.com" target="_blank" rel="noreferrer" className="text-[#D4AF37] hover:underline">
            Pandora Management Consult Ltd
          </a>.
        </p>
        <div className="mt-8 flex flex-wrap gap-8 text-[10px] font-mono tracking-widest text-slate-500 uppercase">
          <span>Artificial Intelligence</span>
          <span className="w-1 h-1 rounded-full bg-slate-700 self-center" />
          <span>Institutional Intelligence</span>
          <span className="w-1 h-1 rounded-full bg-slate-700 self-center" />
          <span>Executive Operations</span>
          <span className="w-1 h-1 rounded-full bg-slate-700 self-center" />
          <span>Strategic Advisory</span>
          <span className="w-1 h-1 rounded-full bg-slate-700 self-center" />
          <span>Workflow Automation</span>
        </div>
      </motion.section>

      {/* Tagline strip */}
      <div className="text-center py-6 border-y border-white/5">
        <div className="font-display text-2xl text-white tracking-tight">
          <span className="text-slate-400">One Platform.</span>{" "}
          <span style={{ color: "var(--gold-500)" }}>Total Institutional Intelligence.</span>
        </div>
      </div>

      {/* 18 Capability Areas */}
      <section>
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="label-eyebrow mb-2">// 18 Integrated Capability Areas</div>
            <h2 className="font-display text-3xl text-white">Core Platform Architecture</h2>
          </div>
          <div className="font-mono text-xs text-slate-500 tracking-widest">INTERCONNECTED INSTITUTIONAL MODULES</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {CAPABILITIES.map((c) => (
            <motion.div
              key={c.num}
              initial={{ opacity: 0, y: 6 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3 }}
              className="nexus-card p-5"
              data-testid={`capability-${c.num}`}
            >
              <div className="flex items-start justify-between mb-3">
                <c.icon className="w-5 h-5" style={{ color: "var(--gold-400)" }} strokeWidth={1.5} />
                <span className="font-mono text-[10px] text-slate-600 tracking-widest">{c.num}</span>
              </div>
              <div className="font-display text-base text-white leading-tight">{c.label}</div>
              <div className="text-xs text-slate-400 mt-2 leading-relaxed">{c.desc}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Specialized Suites */}
      <section>
        <div className="mb-6">
          <div className="label-eyebrow mb-2">// Specialized Institutional Suites</div>
          <h2 className="font-display text-3xl text-white">Four Configurable Operating Systems</h2>
          <p className="text-slate-400 mt-2 max-w-2xl text-sm">
            Pandora Nexus is delivered as four specialized suites, each tailored to a distinct institutional audience.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {SUITES.map((s) => (
            <div key={s.id} className="nexus-card p-6 group" data-testid={`suite-${s.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div
                  className="w-12 h-12 flex items-center justify-center border"
                  style={{ borderColor: s.color, background: `${s.color}10` }}
                >
                  <s.icon className="w-5 h-5" style={{ color: s.color }} strokeWidth={1.5} />
                </div>
                <ArrowUpRight className="w-4 h-4 text-slate-600 group-hover:text-[#D4AF37] transition-colors" />
              </div>
              <div className="font-display text-xl text-white">{s.name}</div>
              <div className="text-xs text-slate-500 font-mono mt-1.5 tracking-widest uppercase">{s.audience}</div>
              <div className="mt-4 pt-4 border-t border-white/5 space-y-2">
                {s.bullets.map((b) => (
                  <div key={b} className="flex items-center gap-2 text-xs text-slate-300">
                    <span className="w-1 h-1" style={{ background: s.color }}></span>
                    <span>{b}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Vision */}
      <section className="border border-[#D4AF37]/30 bg-[#0A111F] p-12 text-center" data-testid="vision-section">
        <div className="label-eyebrow mb-3">// Pandora Nexus Vision</div>
        <div className="font-display text-3xl lg:text-4xl text-white max-w-3xl mx-auto leading-tight">
          To become <span style={{ color: "var(--gold-500)" }}>Africa's AI-powered institutional intelligence and operational infrastructure ecosystem</span>.
        </div>
        <Link
          to="/"
          className="inline-flex mt-8 items-center gap-2 px-6 py-3 font-medium text-sm"
          style={{ background: "var(--gold-500)", color: "#050B14" }}
          data-testid="enter-command-center"
        >
          Enter Command Center <ArrowUpRight className="w-4 h-4" />
        </Link>
      </section>

      <footer className="text-center py-6 text-[10px] font-mono tracking-widest text-slate-600 uppercase">
        Pandora Management Consult Ltd · pandoramanagementconsult.com
      </footer>
    </div>
  );
}
